import { Card, Col, Image, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import { useState, useRef } from "react";
import { useSwipeable } from 'react-swipeable';
import { getMeetingCardDisplayAssignment } from '../../_helpers/meetingCardDisplay';

// import headerImg from '../../assets/logos/TMLogo.png';
import StandardModal from '../general/defaultModals/StandardModal';
import MeetingEditForm from './crudModals/MeetingEditForm';
import MeetingDeleteBody from './crudModals/MeetingDeleteBody';

export default function MeetingsCard({ meeting, getMeetings, editMode, displayRole = '' }) {
  const { user: authUser } = useSelector(x => x.auth);
  const navigate = useNavigate();

  const effectiveDisplayRole = meeting.meetingCardDisplayRole || displayRole;
  const displayAssignment = getMeetingCardDisplayAssignment(meeting.roleAssignments, effectiveDisplayRole);
  // avatarUrl fallback already handled inside getMeetingCardDisplayAssignment helper
  const displayRoleLabel = String(displayAssignment.roleLabel || 'Role').toUpperCase();

  // Swipe state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [swipeDelta, setSwipeDelta] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const swipeRef = useRef(false);
  // const swipeTimeout = useRef(null);
  const [ignoreClick, setIgnoreClick] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const SWIPE_THRESHOLD = 100; // Even lower for testing

  const canEditMeeting = !meeting?.isLocked;

  const handleShowEditModal = () => setShowEditModal(true);
  const handleCloseEditModal = () => setShowEditModal(false);

  const handleShowDeleteModal = () => setShowDeleteModal(true);
  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const handleSwiping = (eventData) => {
    setSwiping(true);
    setSwipeDelta(eventData.deltaX);
  };

  const handleSwiped = (eventData) => {
    setSwiping(false);
    setSwipeDelta(0); // Always reset

    // Debug
    // console.log('Swiped', eventData);

    if (eventData.absX > SWIPE_THRESHOLD) {
      swipeRef.current = true;
      setIgnoreClick(true);
      if (eventData.deltaX > 0) {
        if (canEditMeeting) handleShowEditModal();
      } else if (eventData.deltaX < 0) {
        handleShowDeleteModal();
      }
    } else {
      swipeRef.current = false;
    }
  };

  const swipeHandlers = useSwipeable({
    onSwiping: handleSwiping,
    onSwiped: handleSwiped,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  // Card click navigates to meeting page
  const handleCardClick = () => {
    if (ignoreClick) {
      setIgnoreClick(false); // Reset for next interaction
      return;
    }
    navigate("/meeting/" + meeting._id);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const url = process.env.REACT_APP_URL;
      const response = await fetch(`${url}/meeting/${meeting._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authUser?.token}`
        }
      });
      if (response.ok) {
        if (getMeetings) getMeetings();
        setShowDeleteModal(false);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Col xs={12} lg={6} className="mb-3">
      <section>
        <div
          style={{ position: 'relative', userSelect: swiping ? 'none' : 'auto' }}
          {...(authUser?.user.acl === "Officer" && editMode ? swipeHandlers : {})}
        >
          {/* Left action (swipe right) */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '100%',
              background: swipeDelta > 0 ? '#198754' : 'transparent',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              paddingLeft: 24,
              zIndex: 0,
              transition: 'background 0.2s'
            }}
          >
            {swipeDelta > 30 && canEditMeeting && (
              <span>
                <i className="bi bi-pencil-square" style={{ fontSize: '1.5rem', marginRight: 8 }}></i>
                Edit
              </span>
            )}
          </div>
          {/* Right action (swipe left) */}
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '100%',
              background: swipeDelta < 0 ? '#dc3545' : 'transparent',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: 24,
              transition: 'background 0.2s'
            }}
          >
            {swipeDelta < -30 && (
              <span>
                <i className="bi bi-trash" style={{ fontSize: '1.5rem', marginLeft: 8 }}></i>
                Delete
              </span>
            )}
          </div>
          {/* The card itself */}
          <div
            style={{
              position: 'relative',
              transform: `translateX(${swipeDelta}px)`,
              transition: swiping ? 'none' : 'transform 0.2s',
              userSelect: swiping ? 'none' : 'auto'
            }}
            onClick={handleCardClick}
          >
            <Card className='next-meeting meetings-mock-card-shell meetings-page-card user-card user-mock-card'>
              <Card.Body className='meetings-mock-card-body'>
                <div className='meetings-mock-card'>
                  <div className='meetings-mock-top'>
                    <div className='meetings-mock-date-group'>
                      <span className='meetings-mock-date'>
                        {meeting?.meetingDate || ''}
                      </span>
                      {meeting?.meetingNumber && (
                        <span className='meetings-mock-number'>
                          #{meeting.meetingNumber}
                        </span>
                      )}
                    </div>
                    {meeting?.isLocked && (
                      <span className='meetings-mock-lock-badge' title='This meeting is locked'>
                        <i className='bi bi-lock-fill' aria-hidden='true' />
                      </span>
                    )}
                  </div>

                  <div className='meetings-mock-avatar'>
                    {displayAssignment.avatar
                      ? <Image src={displayAssignment.avatar} alt='Assigned role avatar' />
                      : displayAssignment.initials
                        ? <span className='meetings-mock-avatar-fallback'>{displayAssignment.initials}</span>
                        : <i className='bi bi-person-fill meetings-mock-avatar-fallback'></i>}
                  </div>

                  <h3 className='meetings-mock-title'>{meeting.meetingTheme}</h3>
                  <div className='meetings-mock-separator'></div>
                  <div className='meetings-mock-role-label'>{displayRoleLabel}:</div>
                  <div className='meetings-mock-role-name'>{displayAssignment.displayName}</div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
        {/* Modals */}
        <StandardModal
          show={showEditModal}
          handleClose={handleCloseEditModal}
          title={`Edit Meeting: ${meeting.meetingTheme}`}
          ModalBodyComponent={
            <MeetingEditForm
              meeting={meeting}
              onCancel={handleCloseEditModal}
              formId={`meeting-edit-form-${meeting._id}`}
              getMeetings={getMeetings}
            />
          }
          ModalFooterComponent={
            <>
              <Button variant="danger" onClick={handleCloseEditModal}>
                Cancel
              </Button>
              <Button variant="success" type="submit" form={`meeting-edit-form-${meeting._id}`}>
                Save Meeting
              </Button>
            </>
          }
        />
        <StandardModal
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          title={`Delete Meeting: ${meeting.meetingTheme}`}
          ModalBodyComponent={
            <MeetingDeleteBody
              meeting={meeting}
            />
          }
          ModalFooterComponent={
            <>
              <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deleteLoading}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  handleCloseDeleteModal();
                  handleDelete();
                }}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting meeting ...' : 'Delete Meeting'}
              </Button>
            </>
          }
        />
      </section>
    </Col>
  );
}